import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/Bits";
import { LeadForm } from "@/components/site/LeadForm";
import { useSettings } from "@/context/SettingsContext";
import { CONTACTS, waLink } from "@/lib/contact";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";

export default function Contact() {
  const settings = useSettings();
  useSeo({
    title: "Contact",
    description: "Contact ThinkStack — enterprise AI transformation, headquartered in Singapore.",
    path: "/contact",
  });

  return (
    <div>
      <section className="border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-7xl px-6 md:px-12 pt-16 pb-16 md:pt-20 md:pb-20">
          <Reveal>
            <p className="overline text-restrained-blue mb-6">Contact</p>
            <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.98] text-deep-navy max-w-4xl">What are you trying to transform?</h1>
            <p className="mt-8 text-xl text-charcoal/70 max-w-2xl">Bring us a workflow, operating challenge or transformation priority. We read every enquiry and respond directly.</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <SectionHeading overline="Reach us" title="Direct lines." />
              <div className="mt-8 space-y-6">
                {settings?.email && (
                  <div className="flex gap-4"><Mail className="text-restrained-blue shrink-0" size={20} /><div><p className="overline text-charcoal/50">Email</p><a href={`mailto:${settings.email}`} className="text-charcoal hover:text-restrained-blue">{settings.email}</a></div></div>
                )}
                {settings?.phone && (
                  <div className="flex gap-4"><Phone className="text-restrained-blue shrink-0" size={20} /><div><p className="overline text-charcoal/50">Phone</p><span className="text-charcoal">{settings.phone}</span></div></div>
                )}
                <div className="flex gap-4"><MapPin className="text-restrained-blue shrink-0" size={20} /><div><p className="overline text-charcoal/50">Headquarters</p><span className="text-charcoal">{settings?.address || "Singapore"}</span></div></div>
                <div className="flex gap-4" data-testid="contact-whatsapp">
                  <MessageCircle className="text-[#25D366] shrink-0" size={20} />
                  <div>
                    <p className="overline text-charcoal/50">WhatsApp</p>
                    <div className="flex flex-col gap-1">
                      <a href={waLink(CONTACTS.raman.wa, "Hi ThinkStack — I'd like to speak with your team about enterprise AI.")} target="_blank" rel="noreferrer" className="text-charcoal hover:text-restrained-blue">{CONTACTS.raman.name} · {CONTACTS.raman.phone}</a>
                      <a href={waLink(CONTACTS.kriti.wa, "Hi ThinkStack — I'd like to speak with your team about enterprise AI.")} target="_blank" rel="noreferrer" className="text-charcoal hover:text-restrained-blue">{CONTACTS.kriti.name} · {CONTACTS.kriti.phone}</a>
                    </div>
                  </div>
                </div>
              </div>
              <p className="mt-10 text-sm text-charcoal/50 leading-relaxed border-t border-[#E5E5E5] pt-6">
                We store the details you submit only to respond to your enquiry. We do not share them or use them for unrelated marketing.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.1}>
              <div className="border border-[#E5E5E5] p-8 md:p-10">
                <LeadForm source="contact" testid="contact-form" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
